/**
 * 任务 API
 *
 * 提供单次和批量任务的统一接口
 * 支持任务查询、创建、取消、重试等操作
 *
 * @version 2.0.0 - 重构版本
 * @author Frontend Team
 * @since 2025-10-22
 */

import { http } from '@/utils/http'
import { unwrap, rawcall } from '@/utils/apiClient'
import type { Task } from '@/types/task.domain'
import type {
  CreateSingleTaskRequest,
  CreateBatchTaskRequest,
  TaskListResponse,
  TaskQueryParams,
  RetryTaskRequest,
  UserTaskLimitsResponse,
  UnifiedLimitsResponse
} from '@/types/task.dto'


// ============= API 类 =============

/**
 * 任务 API 类
 * 
 * 设计原则：
 * - 单一职责：只处理任务相关的 API 调用
 * - 类型安全：充分利用 TypeScript 类型系统
 * - 错误处理：统一的错误处理和日志记录
 */
export class TaskAPI {
  private baseURL = '/api/v1/tasks'

  /**
   * 获取单个任务详情
   * 
   * @param taskId 任务ID
   * @returns 任务详情
   */
  async getTask(taskId: string): Promise<Task> {
    return unwrap<Task>(() =>
      http.get(`${this.baseURL}/${taskId}`)
    )
  }

  /**
   * 更新任务
   * 
   * @param taskId 任务ID
   * @param updates 更新内容
   * @returns 更新后的任务
   */
  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    return unwrap<Task>(() =>
      http.put(`${this.baseURL}/${taskId}`, updates)
    )
  }

  /**
   * 批量删除任务
   * 
   * @param taskIds 任务ID列表
   */
  async deleteTasks(taskIds: string[]): Promise<void> {
    return unwrap<void>(() =>
      http.post(`${this.baseURL}/batch-delete`, { taskIds })
    )
  }

  /**
   * 获取任务统计信息
   * 
   * @returns 统计信息
   */
  async getTaskStats(): Promise<{
    total: number
    completed: number
    failed: number
    processing: number
    todayCreated: number
    todayCompleted: number
  }> {
    return unwrap<{
      total: number
      completed: number
      failed: number
      processing: number
      todayCreated: number
      todayCompleted: number
    }>(() =>
      http.get(`${this.baseURL}/stats`)
    )
  }

  /**
   * 获取任务列表
   *
   * @param params 查询参数（分页、过滤、排序）
   * @returns 任务列表响应
   */
  async getTasks(params?: TaskQueryParams): Promise<TaskListResponse> {
    const queryParams = this.buildQueryParams(params)
    return unwrap<TaskListResponse>(() =>
      http.get(this.baseURL, { params: queryParams })
    )
  }



  /**
   * 创建单次任务
   *
   * @param request 创建任务请求
   * @returns 创建的任务
   */
  async createSingleTask(request: CreateSingleTaskRequest): Promise<Task> {
    return unwrap<Task>(() =>
      http.post(this.baseURL, request)
    )
  }

  /**
   * 创建批量任务
   *
   * @param request 创建批量任务请求
   * @returns 创建的任务
   */
  async createBatchTask(request: CreateBatchTaskRequest): Promise<Task> {
    return unwrap<Task>(() =>
      http.post(`${this.baseURL}/batch`, request)
    )
  }

  /**
   * 取消任务
   * 
   * @param taskId 任务ID
   * @returns 更新后的任务
   */
  async cancelTask(taskId: string): Promise<Task> {
    return unwrap<Task>(() =>
      http.post(`${this.baseURL}/${taskId}/cancel`)
    )
  }

  /**
   * 重试任务（整单或任务项）
   *
   * @param taskId 任务ID
   * @param itemIds 任务项ID列表（可选，不指定则重试整单）
   * @returns 更新后的任务
   */
  async retryTask(taskId: string, itemIds?: string[]): Promise<Task> {
    const payload: RetryTaskRequest = { itemIds }
    return unwrap<Task>(() =>
      http.post(`${this.baseURL}/${taskId}/retry`, payload)
    )
  }

  /**
   * 删除任务
   * 
   * @param taskId 任务ID
   */
  async deleteTask(taskId: string): Promise<void> {
    return unwrap<void>(() =>
      http.delete(`${this.baseURL}/${taskId}`)
    )
  }

  /**
   * 获取任务进度（单次查询）
   * 
   * @param taskId 任务ID
   * @returns 任务进度信息
   */
  async getTaskProgress(taskId: string): Promise<Partial<Task>> {
    return unwrap<Partial<Task>>(() =>
      http.get(`${this.baseURL}/${taskId}/progress`)
    )
  }



  /**
   * 下载任务结果
   * 
   * @param taskId 任务ID
   * @returns 下载 URL
   */
  async getDownloadUrl(taskId: string): Promise<string> {
    const response = await unwrap<{ url: string }>(() =>
      http.get(`${this.baseURL}/${taskId}/download`)
    )
    return response.url
  }

  /**
   * 下载任务结果（二进制流或 JSON）
   *
   * @param taskId 任务ID
   * @param format 下载格式，支持 zip 和 json
   * @returns Promise<Blob | any>
   */
  async downloadTaskResults(
    taskId: string,
    format: 'json' | 'zip' = 'zip'
  ): Promise<Blob | any> {
    if (format === 'zip') {
      // 下载二进制文件，使用 rawcall
      const response = await rawcall<Blob>(() =>
        http.get(`${this.baseURL}/${taskId}/download`, {
          params: { format },
          responseType: 'blob'
        })
      )
      return response.data
    } else {
      // 下载JSON数据，使用 unwrap
      return unwrap<any>(() =>
        http.get(`${this.baseURL}/${taskId}/download`, {
          params: { format }
        })
      )
    }
  }

  /**
   * 获取用户任务权益
   *
   * @returns 用户任务权益信息
   */
  async getUserTaskLimits(): Promise<UserTaskLimitsResponse> {
    return unwrap<UserTaskLimitsResponse>(() =>
      http.get(`${this.baseURL}/limits`)
    )
  }

  /**
   * 获取统一用户权益
   *
   * @returns 统一权益信息
   */
  async getUnifiedLimits(): Promise<UnifiedLimitsResponse> {
    return unwrap<UnifiedLimitsResponse>(() =>
      http.get(`${this.baseURL}/unified-limits`)
    )
  }

  // ============= 辅助方法 =============

  /**
   * 构建查询参数
   *
   * @param params 查询参数
   * @returns 转换后的查询参数
   */
  private buildQueryParams(params?: TaskQueryParams): Record<string, any> {
    if (!params) return {}

    const queryParams: Record<string, any> = {}

    if (params.page) queryParams.page = params.page
    if (params.pageSize) queryParams.pageSize = params.pageSize

    // 处理过滤参数
    if (params.filter) {
      const filter = params.filter
      if (filter.status) {
        queryParams.status = Array.isArray(filter.status)
          ? filter.status.join(',')
          : filter.status
      }
      if (filter.type) {
        queryParams.type = Array.isArray(filter.type)
          ? filter.type.join(',')
          : filter.type
      }
      if (filter.keyword) queryParams.keyword = filter.keyword
      if (filter.dateRange) {
        queryParams.startDate = filter.dateRange[0]
        queryParams.endDate = filter.dateRange[1]
      }
      if (filter.voices) queryParams.voices = filter.voices.join(',')
    }

    // 处理排序参数
    if (params.sort) {
      queryParams.sortField = params.sort.field
      queryParams.sortOrder = params.sort.order
    }

    return queryParams
  }

  /**
   * 更新任务项元数据（前端回传精准时长）
   *
   * @param taskId 任务ID
   * @param itemId 任务项ID
   * @param metrics 元数据（时长、文件大小）
   */
  async updateTaskItemMetrics(
    taskId: string,
    itemId: string,
    metrics: { durationSec?: number; fileSize?: number }
  ): Promise<void> {
    return unwrap<void>(() =>
      http.post(`${this.baseURL}/${taskId}/items/${itemId}/metrics`, metrics)
    )
  }
}

// ============= 导出单例 =============

export const taskAPI = new TaskAPI()

